import { z } from "zod";
import type { RoleCode } from "../auth";
import { PAGE_SIZE_MAX, paginationQuery, searchParam } from "../pagination";
import type { NocRef } from "./common";
import type { MeetSportSummary } from "./meets";

/**
 * Para support links — which support person goes with which para athlete
 * (migration 0053, `para_support_links`; the old OVR's "Para Support Links"):
 * a guide runner with a T11 sprinter, a ramp assistant with a BC3 boccia
 * player, a sighted director with a B1 lawn bowler.
 *
 *   GET    /para-support-links?games_id=…                       Paginated<ParaSupportLinkItem>
 *   GET    /para-support-links/candidates?games_id=…&kind=…      ParaSupportCardRef[]   pickers
 *   POST   /para-support-links                                   ParaSupportLinkResult
 *   DELETE /para-support-links/:athleteCardId/:supportCardId     null
 *
 * A link is between two CARDS of one para games (like the VIP escort link):
 * the athlete's card — category `Aa` — and the support person's card, any
 * category (a guide runner is often accredited as an athlete). Many to many:
 * an athlete may have a sprint guide and a relay guide; a director may serve a
 * whole triples team. The link is a lookup: it never enters anyone in an
 * event and never gives a medal — a guide who shares the medal is a GUIDE
 * member of the entry's roster, as before.
 *
 * Reads: `COMPETITION_WRITE_ROLES`, narrowed by scope on the athlete's card.
 * Writes: SUPER_ADMIN only (`PARA_SUPPORT_WRITE_ROLES`), as in the old OVR.
 */

export const PARA_SUPPORT_ROLES = ["GUIDE", "PILOT", "RAMP_ASSISTANT", "SIGHTED_DIRECTOR", "OTHER"] as const;
export type ParaSupportRole = (typeof PARA_SUPPORT_ROLES)[number];

export const PARA_SUPPORT_WRITE_ROLES = ["SUPER_ADMIN"] as const satisfies readonly RoleCode[];
export const PARA_SUPPORT_LINKS_PAGE_SIZE_DEFAULT = 50;
/** Support people linked in one POST. */
export const PARA_SUPPORT_LINK_BATCH_MAX = 10;
/** A picker shows at most this many cards; type more of the name to narrow it. */
export const PARA_SUPPORT_CANDIDATES_MAX = 20;

/** Why one support card of a POST was not linked. */
export const PARA_SUPPORT_REFUSALS = ["NOT_FOUND", "OTHER_GAMES", "SAME_PERSON", "ALREADY_LINKED"] as const;
export type ParaSupportRefusal = (typeof PARA_SUPPORT_REFUSALS)[number];

const idField = z.string().uuid().toLowerCase();

/** `GET /para-support-links` — by athlete name, then support name. `search` matches either name. */
export const listParaSupportLinksQuery = z.object({
	games_id: idField,
	search: searchParam.optional(),
	responsible_organization_id: idField.optional(),
	sport_id: idField.optional(),
	role: z.enum(PARA_SUPPORT_ROLES).optional(),
	/** One athlete card's links (the link form lists them). */
	athlete_ac_card_id: idField.optional(),
	page: paginationQuery.shape.page,
	page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(PARA_SUPPORT_LINKS_PAGE_SIZE_DEFAULT),
});
export type ListParaSupportLinksQuery = z.input<typeof listParaSupportLinksQuery>;

/**
 * `GET /para-support-links/candidates` — cards for the pickers, by name:
 * `athlete` = category `Aa` cards of the games, `support` = any card of the
 * games. Not APPROVED cards are listed too (a link can be prepared early).
 */
export const paraSupportCandidatesQuery = z.object({
	games_id: idField,
	kind: z.enum(["athlete", "support"]),
	search: searchParam,
	responsible_organization_id: idField.optional(),
});
export type ParaSupportCandidatesQuery = z.input<typeof paraSupportCandidatesQuery>;

/** `POST /para-support-links` — link up to `PARA_SUPPORT_LINK_BATCH_MAX` support cards to one athlete card, with one role. */
export const createParaSupportLinksBody = z.object({
	athlete_ac_card_id: idField,
	support_ac_card_ids: z
		.array(idField)
		.min(1)
		.max(PARA_SUPPORT_LINK_BATCH_MAX)
		.refine((ids) => new Set(ids).size === ids.length, "Each support person may appear once"),
	role: z.enum(PARA_SUPPORT_ROLES),
	reason: z.string().trim().min(1).max(500).optional(),
});
export type CreateParaSupportLinksBody = z.input<typeof createParaSupportLinksBody>;

export const paraSupportLinkParams = z.object({ athleteCardId: idField, supportCardId: idField });

export interface ParaSupportCardRef {
	ac_card_id: string;
	people_id: string;
	name: string;
	accreditation_no: number | null;
	registration_no: number;
	/** The card's workflow status (DRAFT … APPROVED). */
	status: string;
	organization: NocRef | null;
	/** The accreditation category code — "Aa", "Ao". */
	category_code: string;
	function_name: string | null;
	sports: MeetSportSummary[];
}

export interface ParaSupportLinkItem {
	athlete: ParaSupportCardRef;
	support: ParaSupportCardRef;
	role: ParaSupportRole;
	created_at: string;
}

export interface ParaSupportLinkResult {
	created: number;
	refused: { ac_card_id: string; reason: ParaSupportRefusal }[];
}
